import { useState } from "react";
import { formatCurrency } from "@/lib/utils";
import { abreWhatsApp } from "@/lib/abreWhatsApp";
import { useAuth } from "@/hooks/use-auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Search, Plus, Edit2, Trash2, Image as ImageIcon, Loader2, Scissors, Clock, X, Pencil, ArrowUpDown, Copy, ExternalLink, CalendarDays } from "lucide-react";
import { CurrencyInput } from "@/components/ui/currency-input";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { customFetch } from "@workspace/api-client-react";
import { extractApiErrorMessage } from "@/lib/api-error";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ReorderDialog } from "@/components/ReorderDialog";
import { unidadeDeMedida } from "@workspace/text";

const LOWERCASE_WORDS = [
  "a", "as", "o", "os", "um", "uma", "uns", "umas",
  "de", "da", "das", "do", "dos", "em", "no", "na", "nos", "nas",
  "num", "numa", "nuns", "numas",
  "ao", "aos", "à", "às",
  "por", "pel", "pelo", "pela", "pelos", "pelas",
  "com", "sem", "sob", "sobre",
  "para", "ante", "após", "até", "contra", "desde", "entre", "perante", "tras",
  "dum", "duma", "duns", "dumas",
  "e", "ou", "mas", "nem", "que", "se", "como"
];

function capitalizeWords(text: string): string {
  return text
    .split(" ")
    .map((word, i, palavras) => {
      if (!word) return word;
      // "500 ml", "1 kg", "2L": a unidade depois do número fica como a loja digitou (o começo dela também, ao digitar).
      if (unidadeDeMedida(palavras, i, true)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

function formatNameForSave(text: string): string {
  return text
    .split(" ")
    .map((word, index, palavras) => {
      if (!word) return word;
      if (unidadeDeMedida(palavras, index)) return word;
      const lower = word.toLowerCase();
      if (index > 0 && LOWERCASE_WORDS.includes(lower)) {
        return lower;
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

interface Service {
  id: string;
  name: string;
  description: string | null;
  price: string;
  durationMinutes: number;
  imageUrl: string | null;
  categoryId: string | null;
  status: string;
  category: { id: string; name: string } | null;
  // Atendido por profissional de inícios fixos (GET /api/services; lib/iniciosFixos do servidor).
  fixedStarts?: boolean;
}

interface ServiceCategory {
  id: string;
  name: string;
}

function useServices(params?: { search?: string; categoryId?: string }) {
  return useQuery<Service[]>({
    queryKey: ["services", params],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (params?.search) qs.set("search", params.search);
      if (params?.categoryId) qs.set("categoryId", params.categoryId);
      return customFetch(`/api/services?${qs.toString()}`);
    },
  });
}

function useServiceCategories() {
  return useQuery<ServiceCategory[]>({
    queryKey: ["service-categories"],
    queryFn: () => customFetch("/api/service-categories"),
  });
}

export default function Services() {
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const { user } = useAuth();
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");
  const agendaUrl = user?.store?.slug ? `${origin}${basePath}/agendar/${user.store.slug}` : "";
  const { data: services = [], isLoading } = useServices({
    search: search || undefined,
    categoryId: selectedCategory !== "all" ? selectedCategory : undefined,
  });
  const { data: allServices = [] } = useServices();
  const { data: categories = [] } = useServiceCategories();
  
  const dialogServices = selectedCategory === "all" && !search ? services : allServices;

  const showCategoryHeaders = selectedCategory === "all" && categories.length > 0 && !search;

  const groupedServices = (() => {
    if (!showCategoryHeaders) return null;
    const groups: { categoryName: string; categoryId: string | null; services: typeof services }[] = [];
    for (const cat of categories) {
      const catServices = services.filter((s) => s.categoryId === cat.id);
      if (catServices.length > 0) {
        groups.push({ categoryName: cat.name, categoryId: cat.id, services: catServices });
      }
    }
    const uncategorized = services.filter((s) => !s.categoryId || !categories.find((c) => c.id === s.categoryId));
    if (uncategorized.length > 0) {
      groups.push({ categoryName: "Sem categoria", categoryId: null, services: uncategorized });
    }
    return groups;
  })();

  const [isModalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Service | null>(null);
  const [reorderOpen, setReorderOpen] = useState(false);


  const queryClient = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();

  const [form, setForm] = useState({ name: "", description: "", price: "", durationMinutes: "60", categoryId: "", status: "ativo", imageUrl: "" });
  const [file, setFile] = useState<File | null>(null);
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [editingCatId, setEditingCatId] = useState<string | null>(null);
  const [editCatName, setEditCatName] = useState("");

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", description: "", price: "", durationMinutes: "60", categoryId: "", status: "ativo", imageUrl: "" });
    setFile(null);
    setShowNewCat(false);
    setNewCatName("");
    setEditingCatId(null);
    setEditCatName("");
    setModalOpen(true);
  };

  const openEdit = (s: Service) => {
    setEditing(s);
    setForm({
      name: s.name,
      description: s.description || "",
      price: s.price,
      durationMinutes: String(s.durationMinutes),
      categoryId: s.categoryId || "",
      status: s.status,
      imageUrl: s.imageUrl || "",
    });
    setFile(null);
    setShowNewCat(false);
    setNewCatName("");
    setEditingCatId(null);
    setEditCatName("");
    setModalOpen(true);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      let imageUrl = form.imageUrl;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const res: any = await customFetch("/api/upload/image", { method: "POST", body: fd, headers: {} });
        imageUrl = res.url;
      }
      const body = {
        name: formatNameForSave(form.name),
        description: form.description || null,
        price: parseFloat(form.price),
        durationMinutes: parseInt(form.durationMinutes) || 60,
        categoryId: form.categoryId || null,
        status: form.status,
        imageUrl: imageUrl || null,
      };
      if (editing) {
        return customFetch(`/api/services/${editing.id}`, { method: "PUT", body: JSON.stringify(body) });
      }
      return customFetch("/api/services", { method: "POST", body: JSON.stringify(body) });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
      setModalOpen(false);
      toast({ title: editing ? "Serviço atualizado!" : "Serviço criado!" });
    },
    onError: (err: unknown) => {
      toast({ title: "Erro", description: extractApiErrorMessage(err), variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => customFetch(`/api/services/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
      toast({ title: "Serviço excluído" });
    },
  });

  const createCatMutation = useMutation({
    mutationFn: (name: string) => customFetch<ServiceCategory>("/api/service-categories", { method: "POST", body: JSON.stringify({ name }) }),
    onSuccess: (newCat) => {
      queryClient.invalidateQueries({ queryKey: ["service-categories"] });
      setShowNewCat(false);
      setNewCatName("");
      if (newCat?.id) setForm((f) => ({ ...f, categoryId: newCat.id }));
      toast({ title: "Categoria criada!" });
    },
  });

  const updateCatMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      customFetch<ServiceCategory>(`/api/service-categories/${id}`, { method: "PUT", body: JSON.stringify({ name }) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["service-categories"] });
      setEditingCatId(null);
      setEditCatName("");
      toast({ title: "Categoria atualizada!" });
    },
  });

  const deleteCatMutation = useMutation({
    mutationFn: (id: string) => customFetch(`/api/service-categories/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["service-categories"] });
      queryClient.invalidateQueries({ queryKey: ["services"] });
      setForm((f) => ({ ...f, categoryId: "" }));
      toast({ title: "Categoria excluída!" });
    },
  });

  return (
    <div className="space-y-6 pb-20 md:pb-0">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Serviços</h1>
          <p className="text-muted-foreground mt-1">Gerencie seus serviços, preços e duração.</p>
        </div>
      </div>

      <div className="flex flex-col gap-4 w-full">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-secondary" />
              <span className="text-sm font-semibold text-secondary">Link da Agenda:</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="icon" className="h-9 w-9 shrink-0 cursor-pointer border-primary/30 text-primary hover:bg-primary/10" onClick={() => navigator.clipboard.writeText(agendaUrl)} title="Copiar"><Copy className="w-4 h-4" /></Button>
              <a href={agendaUrl} target="_blank" rel="noopener noreferrer"><Button variant="outline" size="icon" className="h-9 w-9 cursor-pointer border-primary/30 text-primary hover:bg-primary/10"><ExternalLink className="w-4 h-4" /></Button></a>
              <Button variant="outline" size="icon" className="h-9 w-9 shrink-0 cursor-pointer text-[#25D366] border-[#25D366]/40 hover:bg-[#25D366]/10" onClick={() => { const message = `Confira nossa agenda de agendamentos: ${agendaUrl}`; abreWhatsApp(`https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`); }}><WhatsAppIcon className="w-4 h-4" /></Button>
            </div>
          </div>
          <Input value={agendaUrl.replace(/^https?:\/\//, "")} readOnly className="h-9 text-xs font-mono w-full bg-background border-primary/30" onClick={(e) => (e.target as HTMLInputElement).select()} />
        </div>
      </div>

      <div className="flex gap-2">
        <Button variant="outline" onClick={() => setReorderOpen(true)} className="flex-1">
          <ArrowUpDown className="w-4 h-4 mr-2" />
          Ordenar
        </Button>
        <Button onClick={openCreate} className="flex-1 shadow-md shadow-primary/20">
          <Plus className="w-4 h-4 mr-2" />
          Serviço
        </Button>
      </div>

      <Card className="overflow-hidden">
        <div className="p-4 border-b border-border/50 bg-muted/10 flex flex-col sm:flex-row gap-4 justify-between">
          <div className="relative max-w-sm w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input placeholder="Buscar serviços..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-background" />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0 hide-scrollbar">
            <Button variant={selectedCategory === "all" ? "default" : "outline"} size="sm" onClick={() => setSelectedCategory("all")}>Todos</Button>
            {categories.map((cat) => (
              <Button key={cat.id} variant={selectedCategory === cat.id ? "default" : "outline"} size="sm" onClick={() => setSelectedCategory(cat.id)}>{cat.name}</Button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : services.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <Scissors className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p className="font-medium">Nenhum serviço cadastrado</p>
            <p className="text-sm mt-1">Clique em "+ Serviço" para começar.</p>
          </div>
        ) : groupedServices ? (
          <div className="divide-y divide-border/50">
            {groupedServices.map((group, gi) => (
              <div key={group.categoryId ?? `__no_cat_${gi}`}>
                <div className="px-4 py-2 bg-muted/30 border-b border-border/50">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {group.categoryName}
                  </p>
                </div>
                <div className="divide-y divide-border/50">
                  {group.services.map((s) => (
                    <ServiceRow key={s.id} service={s} showCategory={false} onEdit={openEdit} onDelete={(id) => deleteMutation.mutate(id)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="divide-y divide-border/50">
            {services.map((s) => (
              <ServiceRow key={s.id} service={s} showCategory={selectedCategory !== "all"} onEdit={openEdit} onDelete={(id) => deleteMutation.mutate(id)} />
            ))}
          </div>
        )}
      </Card>

      <Dialog open={isModalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Editar Serviço" : "Novo Serviço"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-4 items-start">
              <div className="w-24 h-24 bg-muted rounded-xl border-2 border-dashed border-border flex items-center justify-center overflow-hidden relative group shrink-0">
                {file ? (
                  <img src={URL.createObjectURL(file)} className="w-full h-full object-cover" />
                ) : form.imageUrl ? (
                  <img src={form.imageUrl} className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-8 h-8 text-muted-foreground/50" />
                )}
                <label className="absolute inset-0 bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity text-xs font-semibold">
                  Trocar
                  <input type="file" className="hidden" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                </label>
              </div>
              <div className="flex-1 min-w-0 space-y-2">
                <label className="flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer hover:bg-muted transition-colors text-sm w-full">
                  <ImageIcon className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">{file ? file.name : (form.imageUrl ? "Trocar imagem" : "Escolher imagem")}</span>
                  <input type="file" className="hidden" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                </label>
                <Label>Nome</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: capitalizeWords(e.target.value) })} />
              </div>
            </div>
            <div className="space-y-2"><Label>Descrição</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Preço</Label><CurrencyInput value={form.price} onChange={(v) => setForm({ ...form, price: v })} /></div>
              <div className="space-y-2"><Label>Duração (min)</Label><Input type="number" value={form.durationMinutes} onChange={(e) => setForm({ ...form, durationMinutes: e.target.value })} data-testid="servico-duracao" /></div>
            </div>
            {/* Frase do Pedro (13/10/2026), palavra por palavra. Ao salvar, o servidor refaz as janelas com os mesmos inícios. */}
            {editing?.fixedStarts && form.durationMinutes !== String(editing.durationMinutes) && (
              <p className="text-xs text-amber-700 dark:text-amber-400" data-testid="servico-aviso-inicios-fixos">
                Este serviço usa horários de início fixos: mudar a duração não muda os horários de início. Para ter outros horários, ajuste os horários dos serviços
              </p>
            )}
            <div className="space-y-2">
              <Label>Categoria</Label>
              {showNewCat ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={newCatName}
                    onChange={(e) => setNewCatName(e.target.value)}
                    placeholder="Nome da nova categoria"
                    className="flex-1 ring-2 ring-primary focus-visible:ring-primary"
                    autoFocus
                    onKeyDown={(e) => { if (e.key === "Enter" && newCatName.trim()) { e.preventDefault(); createCatMutation.mutate(newCatName.trim()); } }}
                  />
                  <Button
                    className="bg-primary hover:bg-primary/90 text-white shrink-0 h-10"
                    onClick={() => { if (newCatName.trim()) createCatMutation.mutate(newCatName.trim()); }}
                    disabled={!newCatName.trim() || createCatMutation.isPending}
                  >
                    {createCatMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Criar"}
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    className="shrink-0 h-10 w-10"
                    onClick={() => { setShowNewCat(false); setNewCatName(""); }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : editingCatId ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={editCatName}
                    onChange={(e) => setEditCatName(e.target.value)}
                    placeholder="Nome da categoria"
                    className="flex-1 ring-2 ring-primary focus-visible:ring-primary"
                    autoFocus
                    onKeyDown={(e) => { if (e.key === "Enter" && editCatName.trim()) { e.preventDefault(); updateCatMutation.mutate({ id: editingCatId, name: editCatName.trim() }); } }}
                  />
                  <Button
                    type="button"
                    className="bg-primary hover:bg-primary/90 text-white shrink-0 h-10"
                    onClick={() => { if (editCatName.trim()) updateCatMutation.mutate({ id: editingCatId, name: editCatName.trim() }); }}
                    disabled={!editCatName.trim() || updateCatMutation.isPending}
                  >
                    {updateCatMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="shrink-0 h-10 w-10"
                    onClick={() => { setEditingCatId(null); setEditCatName(""); }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <select className="flex-1 min-w-0 h-10 border rounded-md p-2 text-sm bg-background" value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}>
                    <option value="">Selecionar categoria...</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  {form.categoryId ? (
                    <>
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        className="shrink-0 h-10 w-10 text-primary hover:text-primary hover:bg-primary/10"
                        onClick={() => {
                          const cat = categories.find(c => c.id === form.categoryId);
                          if (cat) { setEditingCatId(cat.id); setEditCatName(cat.name); }
                        }}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        className="shrink-0 h-10 w-10 text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={async () => {
                          if (await confirm({
                            title: "Excluir esta categoria?",
                            description: "Os serviços dela ficarão sem categoria.",
                            confirmLabel: "Excluir",
                          })) {
                            deleteCatMutation.mutate(form.categoryId);
                          }
                        }}
                        disabled={deleteCatMutation.isPending}
                      >
                        {deleteCatMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="icon"
                      variant="outline"
                      className="shrink-0 h-10 w-10"
                      onClick={() => setShowNewCat(true)}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <select className="w-full border rounded-md p-2 text-sm bg-background" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="ativo">Ativo</option>
                <option value="indisponivel">Indisponível no Momento</option>
              </select>
            </div>
          </div>
          <DialogFooter className="!flex-row gap-2 justify-end">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !form.name || !form.price}>
              {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReorderDialog
        open={reorderOpen}
        onOpenChange={setReorderOpen}
        tabs={[
          {
            key: "categories",
            label: "Categorias",
            items: categories.map((c) => ({ id: c.id, name: c.name })),
            onSave: async (items) => {
              await customFetch("/api/service-categories/reorder", {
                method: "PUT",
                body: JSON.stringify({ items }),
              });
              queryClient.invalidateQueries({ queryKey: ["service-categories"] });
              queryClient.invalidateQueries({ queryKey: ["services"] });
              toast({ title: "Ordem das categorias salva!" });
            },
          },
          {
            key: "services",
            label: "Serviços",
            sections: (() => {
              const sections: { label: string; items: { id: string; name: string }[] }[] = [];
              for (const cat of categories) {
                const catServices = dialogServices.filter((s) => s.categoryId === cat.id);
                if (catServices.length > 0) {
                  sections.push({ label: cat.name, items: catServices.map((s) => ({ id: s.id, name: s.name })) });
                }
              }
              const uncategorized = dialogServices.filter((s) => !s.categoryId || !categories.find((c) => c.id === s.categoryId));
              if (uncategorized.length > 0) {
                sections.push({ label: "Sem categoria", items: uncategorized.map((s) => ({ id: s.id, name: s.name })) });
              }
              if (sections.length === 0) {
                sections.push({ label: "Serviços", items: dialogServices.map((s) => ({ id: s.id, name: s.name })) });
              }
              return sections;
            })(),
            onSave: async (items) => {
              await customFetch("/api/services/reorder", {
                method: "PUT",
                body: JSON.stringify({ items }),
              });
              queryClient.invalidateQueries({ queryKey: ["services"] });
              toast({ title: "Ordem dos serviços salva!" });
            },
          },
        ]}
      />
    </div>
  );
}

function ServiceRow({
  service: s,
  showCategory,
  onEdit,
  onDelete,
}: {
  service: Service & { category?: { id: string; name: string; sortOrder: number } | null };
  showCategory: boolean;
  onEdit: (s: Service & { category?: { id: string; name: string; sortOrder: number } | null }) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="flex items-center gap-4 p-4 hover:bg-muted/30 transition-colors">
      <div className="w-14 h-14 rounded-xl bg-muted/50 flex items-center justify-center overflow-hidden flex-shrink-0">
        {s.imageUrl ? <img src={s.imageUrl} alt={s.name} className="w-full h-full object-cover" /> : <Scissors className="w-6 h-6 text-muted-foreground" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-semibold truncate">{s.name}</p>
          {showCategory && s.category && <span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground">{s.category.name}</span>}
          {s.status === "indisponivel" && <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full">Indisponível</span>}
        </div>
        <p className="text-sm text-muted-foreground">{s.durationMinutes}min • {formatCurrency(Number(s.price))}</p>
      </div>
      <div className="flex gap-1">
        <Button variant="ghost" size="icon" onClick={() => onEdit(s)}><Edit2 className="w-4 h-4" /></Button>
        <Button variant="ghost" size="icon" className="text-destructive" onClick={() => onDelete(s.id)}><Trash2 className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}
